/**
 * Easy Read 요청 — Mini App 표현층
 *
 * Slack 과 같이 확인을 한 번 받는다. 요청 하나가 LLM 호출 하나라 실수로 눌리면
 * 비용과 rate limit 이 그대로 나간다 — 재요청이 흡수된다는 것과는 다른 문제다.
 *
 * 상태 조회는 버튼마다 왕복하지 않는다. 화면에 뜬 버튼들이 스스로 등록하면
 * Provider 가 한 번에 모아 조회한다 — 논문 열 개짜리 리포트에서 요청 열 번이 나가지 않게.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { haptic } from '../../hooks/useTelegram';
import { useAuth } from '../../context/AuthContext';

const API_URL = '/api';

// Slack 과 같은 기준. 한글 기사는 이미 읽을 수 있어 정리할 이유가 없다.
export const ENGLISH_SITES = new Set(['HackerNews', 'TechnologyReview', 'TechCrunch']);

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const keyOf = (sourceType, sourceId) => `${sourceType}:${sourceId}`;

const EasyReadContext = createContext(null);

export const EasyReadProvider = ({ children }) => {
    // Mini App 로그인은 비동기다. 토큰이 오기 전에 조회하면 401 로 버튼이 통째로 사라진다.
    const { isAuthenticated, loading: authLoading } = useAuth();

    const [statuses, setStatuses] = useState({});
    const [enabled, setEnabled] = useState(true);
    const [available, setAvailable] = useState(true);

    const pending = useRef(new Map()); // key → {source_type, source_id}
    const timer = useRef(null);
    const asked = useRef(new Set());

    const flush = useCallback(async () => {
        const items = Array.from(pending.current.values());
        pending.current.clear();
        if (items.length === 0) return;

        try {
            const { data } = await axios.post(
                `${API_URL}/easy-read/status`,
                { items },
                { headers: authHeaders() }
            );
            setEnabled(data.enabled !== false);
            setStatuses((prev) => ({ ...prev, ...(data.statuses || {}) }));
        } catch (e) {
            // 로그인하지 않았거나(401) 서버가 이 API 를 모르면 버튼 자체를 감춘다.
            setAvailable(false);
        }
    }, []);

    const register = useCallback(
        (sourceType, sourceId) => {
            const key = keyOf(sourceType, sourceId);
            if (asked.current.has(key)) return;
            asked.current.add(key);
            pending.current.set(key, { source_type: sourceType, source_id: sourceId });

            if (authLoading || !isAuthenticated) return; // 로그인 후 아래 effect 가 모아서 조회한다
            clearTimeout(timer.current);
            timer.current = setTimeout(flush, 0); // 같은 렌더에서 등록된 것들을 한 묶음으로
        },
        [flush, authLoading, isAuthenticated]
    );

    // 로그인이 늦게 끝난 경우, 그 사이 등록된 항목들을 한 번에 조회한다.
    useEffect(() => {
        if (!authLoading && isAuthenticated && pending.current.size > 0) flush();
    }, [authLoading, isAuthenticated, flush]);

    const request = useCallback(async (sourceType, sourceId) => {
        const key = keyOf(sourceType, sourceId);
        setStatuses((prev) => ({ ...prev, [key]: { status: 'sending' } }));
        try {
            const { data } = await axios.post(
                `${API_URL}/easy-read/request`,
                { source_type: sourceType, source_id: sourceId },
                { headers: authHeaders() }
            );
            setStatuses((prev) => ({ ...prev, [key]: data }));
        } catch (e) {
            if (e.response?.status === 503) setEnabled(false);
            setStatuses((prev) => ({ ...prev, [key]: { status: 'error' } }));
        }
    }, []);

    useEffect(() => () => clearTimeout(timer.current), []);

    return (
        <EasyReadContext.Provider value={{ statuses, enabled, available, register, request }}>
            {children}
        </EasyReadContext.Provider>
    );
};

const LABELS = {
    idle: '📝 쉽게 읽기',
    sending: '요청 중…',
    requested: '요청됨 · 완료되면 알려드려요',
    pending: '만드는 중 · 완료되면 알려드려요',
    resent: '이미 있어요 · 다시 보내드렸어요',
    completed: '📨 다시 보내기',
    failed: '📝 다시 요청하기',
    error: '실패했어요 · 다시 눌러보세요',
};

// 눌러도 되는 상태. 진행 중인 요청을 또 누르게 두지 않는다.
const ACTIONABLE = new Set(['idle', 'completed', 'failed', 'error']);

/**
 * 논문/영문 기사 한 건에 붙는 Easy Read 버튼.
 * Provider 밖(일반 웹)에서는 아무것도 그리지 않는다 — 웹 화면은 그대로 둔다.
 */
export const EasyReadButton = ({ sourceType, sourceId }) => {
    const ctx = useContext(EasyReadContext);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        if (ctx && sourceId) ctx.register(sourceType, sourceId);
    }, [ctx, sourceType, sourceId]);

    if (!ctx || !sourceId) return null;
    const { statuses, enabled, available, request } = ctx;
    if (!available || !enabled) return null;

    const state = statuses[keyOf(sourceType, sourceId)]?.status || 'idle';
    const actionable = ACTIONABLE.has(state);

    // 확인 단계. 요청 하나가 LLM 호출 하나라 실수로 눌린 것을 여기서 거른다.
    if (confirming && actionable) {
        return (
            <div className="tg-item-actions tg-confirm">
                <span className="tg-confirm-q">
                    {state === 'completed' ? '알림을 다시 보낼까요?' : '글 형태로 정리를 요청할까요?'}
                </span>
                <div className="tg-confirm-btns">
                    <button
                        type="button"
                        className="tg-item-action is-primary"
                        onClick={() => {
                            haptic('medium');
                            setConfirming(false);
                            request(sourceType, sourceId);
                        }}
                    >
                        네, 요청합니다
                    </button>
                    <button
                        type="button"
                        className="tg-item-action"
                        onClick={() => {
                            haptic('light');
                            setConfirming(false);
                        }}
                    >
                        아니오
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-item-actions">
            <button
                type="button"
                className={`tg-item-action${actionable ? '' : ' is-done'}`}
                disabled={!actionable}
                onClick={() => {
                    haptic('light');
                    setConfirming(true);
                }}
            >
                {LABELS[state] || LABELS.idle}
            </button>
        </div>
    );
};

/**
 * ReportRenderer 에 넘길 항목별 액션 렌더러.
 * 논문은 전부, 기사는 영문 사이트만 대상이다.
 */
export const easyReadItemActions = ({ source_type: sourceType, source_id: sourceId, source }) => {
    if (sourceType === 'article' && !ENGLISH_SITES.has(source)) return null;
    return <EasyReadButton sourceType={sourceType} sourceId={sourceId} />;
};
